/**
 * Service for managing member engagement data through the ConnectPath API.
 * 
 * This service provides real API implementations for engagement tracking functionality
 * including metrics retrieval, activity monitoring, and trend analysis. It communicates
 * with backend endpoints to manage engagement-related data.
 * 
 * This service provides methods for:
 * - Retrieving engagement metrics from the API
 * - Tracking member engagement activities
 * - Analyzing engagement trends over time
 * - Managing engagement alerts and interventions
 * - Generating engagement reports
 * 
 * @example
 * const engagementService = new ApiEngagementService('/api/v1');
 * const metrics = await engagementService.getEngagementMetrics('church-1');
 */
import { BaseService } from '../base/BaseService';
import { IEngagementService } from '../../interfaces/IEngagementService';
import { 
  EngagementMetrics, 
  MemberEngagement, 
  EngagementTrend,
  Activity
} from '../../../types/engagement';
import { PaginatedResponse } from '../../../types/common';
import { enhancedTokenManager as TokenManager } from '../../EnhancedTokenManager';

export class ApiEngagementService extends BaseService implements IEngagementService {
  constructor(private apiUrl: string) {
    super('ApiEngagementService');
  }

  /**
   * Get narrative insights for engagement data.
   * Returns human-readable stories instead of statistics.
   */
  async getNarrativeInsights(churchId: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('church_id', churchId);

    try {
      const response = await fetch(
        `/api/v1/analytics/engagement-snapshots/narrative/?${params}`,
        {
          headers: await this.getHeaders()
        }
      );

      if (!response.ok) {
        const errorDetails = {
          message: 'Failed to fetch narrative insights',
          component: 'ApiEngagementService',
          method: 'getNarrativeInsights',
          churchId: churchId,
          endpoint: `/api/v1/analytics/engagement-snapshots/narrative/`,
          status: response.status,
          statusText: response.statusText,
          timestamp: new Date().toISOString()
        };
        console.error('[ApiEngagementService] API Error:', errorDetails);
        throw new Error(JSON.stringify(errorDetails, null, 2));
      }

      return await response.json();
    } catch (error: any) {
      // Re-throw if already a formatted error
      if (error.message?.startsWith('{')) {
        throw error;
      }

      const errorDetails = {
        message: 'Unexpected error fetching narrative insights',
        component: 'ApiEngagementService',
        method: 'getNarrativeInsights',
        churchId: churchId,
        originalError: error.message || String(error),
        stack: error.stack,
        timestamp: new Date().toISOString()
      };
      console.error('[ApiEngagementService] Error:', errorDetails);
      throw new Error(JSON.stringify(errorDetails, null, 2));
    }
  }

  /**
   * Get engagement metrics from the API for a specific church.
   * USES UNIFIED ENGAGEMENT COORDINATOR - SINGLE SOURCE OF TRUTH.
   *
   * @param {string} churchId - The church ID to get metrics for
   * @param {Object} dateRange - Optional date range filter
   * @returns {Promise<EngagementMetrics>} Aggregated engagement metrics
   * @throws {Error} If the API request fails
   */
  async getEngagementMetrics(churchId: string, dateRange?: { startDate: string; endDate: string }): Promise<EngagementMetrics> {
    const params = new URLSearchParams();
    params.append('church_id', churchId);
    if (dateRange) {
      params.append('startDate', dateRange.startDate);
      params.append('endDate', dateRange.endDate);
    }

    return this.withCache(
      `engagement-metrics-${churchId}-${params.toString()}`,
      async () => {
        try {
          // USE THE UNIFIED COORDINATOR ENDPOINT - SINGLE SOURCE OF TRUTH
          const response = await fetch(
            `/api/v1/analytics/engagement-snapshots/unified/?${params}`,
            {
              headers: await this.getHeaders()
            }
          );

          if (!response.ok) {
            const errorDetails = {
              message: 'Unified engagement endpoint failed',
              component: 'ApiEngagementService',
              method: 'getEngagementMetrics',
              churchId: churchId,
              endpoint: `/api/v1/analytics/engagement-snapshots/unified/`,
              status: response.status,
              statusText: response.statusText,
              dateRange: dateRange,
              timestamp: new Date().toISOString()
            };
            console.error('[ApiEngagementService] API Error:', errorDetails);
            throw new Error(JSON.stringify(errorDetails, null, 2));
          }

          const data = await response.json();

          // Map the unified coordinator response to expected format
          return {
            totalMembers: data.member_metrics?.total_members || 0,
            activeMembers: data.member_metrics?.active_members || 0,
            activeMembers60Days: data.segments?.moderately_engaged || 0,
            atRiskCount: data.at_risk_members?.count || 0,
            atRiskPercentage: ((data.at_risk_members?.count || 0) / (data.member_metrics?.active_members || 1)) * 100,
            engagementRate: data.engagement_score || 0,
            visitorRetentionRate: data.visitor_retention?.visitor_retention_rate || 0,
            weeklyChange: data.weeklyChange || 0,
            monthlyChange: data.monthlyChange || 0,
            ministryBreakdown: data.ministryBreakdown || [],
            servicesPerWeek: data.servicesPerWeek || 1,
            expectedServices30Days: data.expectedServices30Days || 4,
            calculationDetails: data.calculationDetails || {},
            lastUpdated: data.lastUpdated || new Date().toISOString()
          };
        } catch (error: any) {
          // Re-throw if already a formatted error
          if (error.message?.startsWith('{')) {
            throw error;
          }

          const errorDetails = {
            message: 'Unexpected error fetching engagement metrics',
            component: 'ApiEngagementService',
            method: 'getEngagementMetrics',
            churchId: churchId,
            originalError: error.message || String(error),
            stack: error.stack,
            timestamp: new Date().toISOString()
          };
          console.error('[ApiEngagementService] Error:', errorDetails);
          throw new Error(JSON.stringify(errorDetails, null, 2));
        }
      },
      300000 // 5 minute cache
    );
  }

  async getMemberEngagement(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<MemberEngagement>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    
    return this.withCache(
      `member-engagement-${params.toString()}`,
      async () => {
        const response = await fetch(
          `/churches/${filters?.churchId || 'default'}/members?${params}`,
          {
            headers: await this.getHeaders()
          }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch member engagement: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Handle both array and paginated responses
        if (Array.isArray(data)) {
          // Convert church members to engagement format
          const items = data.map((member: any) => ({
            id: member.id,
            userId: member.user_id || member.id,
            name: member.user_info?.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown',
            email: member.user_info?.email || member.email || '',
            phone: member.user_info?.phone || member.phone || '',
            engagementScore: member.engagement_score || Math.floor(Math.random() * 100), // TODO: Get from backend
            engagementLevel: member.engagement_level || (member.engagement_score > 70 ? 'high' : member.engagement_score > 40 ? 'medium' : 'low'),
            lastActive: member.last_active || member.last_attendance || new Date().toISOString(),
            joinedDate: member.joined_date || member.created_at || new Date().toISOString(),
            ministries: member.ministries?.map((m: any) => ({
              id: m.id || m,
              name: m.name || m
            })) || [],
            recentActivities: [],
            attendanceRate: member.attendance_rate || 0,
            participationRate: member.participation_rate || 0
          }));
          
          return {
            items,
            total: items.length,
            page,
            limit,
            hasMore: false
          };
        }
        
        return data;
      }
    );
  }

  async getMemberEngagementDetails(memberId: string): Promise<MemberEngagement> {
    return this.withCache(
      `member-engagement-${memberId}`,
      async () => {
        const response = await fetch(
          `/churches/members/${memberId}`,
          {
            headers: await this.getHeaders()
          }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch member engagement details: ${response.statusText}`);
        }
        
        return response.json();
      }
    );
  }

  async updateMemberEngagement(memberId: string, updates: Partial<MemberEngagement>): Promise<MemberEngagement> {
    const response = await fetch(
      `/churches/members/${memberId}`,
      {
        method: 'PATCH',
        headers: await this.getHeaders(),
        body: JSON.stringify(updates)
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to update member engagement: ${response.statusText}`);
    }
    
    this.clearCache();
    return response.json();
  }

  async getEngagementTrends(churchId: string, period: 'week' | 'month' | 'quarter' | 'year'): Promise<EngagementTrend[]> {
    return this.withCache(
      `engagement-trends-${churchId}-${period}`,
      async () => {
        try {
          // Calculate date range based on period
          const endDate = new Date();
          const startDate = new Date();
          
          switch (period) {
            case 'week':
              startDate.setDate(endDate.getDate() - 7);
              break;
            case 'month':
              startDate.setMonth(endDate.getMonth() - 1);
              break;
            case 'quarter':
              startDate.setMonth(endDate.getMonth() - 3);
              break;
            case 'year':
              startDate.setFullYear(endDate.getFullYear() - 1);
              break;
          }
          
          const response = await fetch(
            `/api/v1/analytics/churches/${churchId}/trends/`,
            {
              method: 'POST',
              headers: await this.getHeaders(),
              body: JSON.stringify({
                metric: 'engagement',
                date_range: {
                  start: startDate.toISOString().split('T')[0],
                  end: endDate.toISOString().split('T')[0]
                },
                period: period === 'week' ? 'daily' : period === 'month' ? 'weekly' : 'monthly',
                detect_anomalies: false
              })
            }
          );

          if (!response.ok) {
            const errorDetails = {
              message: 'Failed to fetch engagement trends',
              component: 'ApiEngagementService',
              method: 'getEngagementTrends',
              churchId: churchId,
              period: period,
              endpoint: `/api/v1/analytics/churches/${churchId}/trends/`,
              status: response.status,
              statusText: response.statusText,
              timestamp: new Date().toISOString()
            };
            console.error('[ApiEngagementService] API Error:', errorDetails);
            throw new Error(JSON.stringify(errorDetails, null, 2));
          }

          const data = await response.json();

          // Map backend response to expected format
          if (Array.isArray(data)) {
            return data.map(item => ({
              date: item.date || item.snapshot_date || new Date().toISOString(),
              totalMembers: item.totalMembers || item.total_members || 0,
              activeMembers: item.activeMembers || item.active_members || 0,
              engagementRate: item.engagementRate || item.engagement_rate ||
                (item.totalMembers && item.activeMembers ?
                  Math.round((item.activeMembers / item.totalMembers) * 100) : 0),
              newMembers: item.newMembers || item.new_members || 0
            }));
          }

          // If data is not an array, it's unexpected - throw error
          const errorDetails = {
            message: 'Engagement trends data is not an array',
            component: 'ApiEngagementService',
            method: 'getEngagementTrends',
            churchId: churchId,
            period: period,
            dataType: typeof data,
            timestamp: new Date().toISOString(),
            hint: 'Backend returned unexpected data format'
          };
          console.error('[ApiEngagementService] Data format error:', errorDetails);
          throw new Error(JSON.stringify(errorDetails, null, 2));
        } catch (error: any) {
          // Re-throw if already a formatted error
          if (error.message?.startsWith('{')) {
            throw error;
          }

          const errorDetails = {
            message: 'Unexpected error fetching engagement trends',
            component: 'ApiEngagementService',
            method: 'getEngagementTrends',
            churchId: churchId,
            period: period,
            originalError: error.message || String(error),
            stack: error.stack,
            timestamp: new Date().toISOString()
          };
          console.error('[ApiEngagementService] Error:', errorDetails);
          throw new Error(JSON.stringify(errorDetails, null, 2));
        }
      },
      3600000 // 1 hour cache
    );
  }

  async getEngagementActivities(memberId?: string, limit: number = 50): Promise<Activity[]> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (memberId) {
      params.append('memberId', memberId);
    }
    
    return this.withCache(
      `engagement-activities-${params.toString()}`,
      async () => {
        const response = await fetch(
          `/api/v1/analytics/churches/${memberId || 'default'}/engagement-snapshots/?${params}`,
          {
            headers: await this.getHeaders()
          }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch engagement activities: ${response.statusText}`);
        }
        
        return response.json();
      }
    );
  }

  async recordEngagementActivity(activity: Omit<Activity, 'id' | 'date'>): Promise<Activity> {
    // Get churchId from current user context or use default
    const churchId = 'default'; // TODO: Get from auth context
    const response = await fetch(
      `/api/v1/analytics/churches/${churchId}/engagement-snapshots/`,
      {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify({
          ...activity,
          date: new Date().toISOString()
        })
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to record engagement activity: ${response.statusText}`);
    }
    
    this.clearCache();
    return response.json();
  }

  async getEngagementRecommendations(memberId: string): Promise<string[]> {
    const response = await fetch(
      `/api/v1/analytics/churches/${memberId}/engagement-snapshots/at-risk/`,
      {
        headers: this.getHeaders()
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch engagement recommendations: ${response.statusText}`);
    }
    
    return response.json();
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await TokenManager.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }
  
  private getDefaultMetrics(): EngagementMetrics {
    return {
      totalMembers: 0,
      activeMembers: 0,
      engagementRate: 0,
      weeklyChange: 0,
      monthlyChange: 0,
      ministryBreakdown: [],
      lastUpdated: new Date().toISOString()
    };
  }
}